import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { useCombobox } from "../../hooks/use-combobox";
import { Text } from "../Text";
import { Chip } from "../Chip";
import { SearchInputField } from "../SearchInput/parts/SearchInputField";
import { SearchLoading } from "../SearchInput/parts/SearchLoading";
import { SearchEmpty } from "../SearchInput/parts/SearchEmpty";
import { AutocompleteOption } from "./AutocompleteOption";
import type { AutocompleteInputSlotProps, AutocompleteProps, OptionState } from "./types";

// ---------------------------------------------------------------------------
// Default filter: case-insensitive substring match on label
// ---------------------------------------------------------------------------

function defaultFilter<T>(
  options: readonly T[],
  inputValue: string,
  getOptionLabel: (option: T) => string,
): readonly T[] {
  if (!inputValue) return options;
  const lower = inputValue.toLowerCase();
  return options.filter((o) => getOptionLabel(o).toLowerCase().includes(lower));
}

// ---------------------------------------------------------------------------
// Autocomplete component
// ---------------------------------------------------------------------------

function AutocompleteInner<T>(props: AutocompleteProps<T>) {
  const {
    options,
    value,
    onChange,
    getOptionLabel,
    getOptionId,
    inputValue: controlledInputValue,
    onInputChange,
    filterOptions,
    isOptionEqualToValue,
    isLoading = false,
    placeholder = "Select…",
    label,
    textInputProps,
    leftContent,
    groupBy,
    renderOption,
    renderGroup,
    renderInput,
    renderLoading,
    renderEmpty,
  } = props;

  // --- Input value (controlled or uncontrolled) ---
  // Input text is always an independent search query — never overwritten by the
  // selected value's label.
  const [internalInputValue, setInternalInputValue] = useState("");
  const inputIsControlled = controlledInputValue !== undefined;
  const inputValue = inputIsControlled ? controlledInputValue : internalInputValue;

  const handleInputChange = useCallback(
    (text: string) => {
      if (!inputIsControlled) {
        setInternalInputValue(text);
      }
      onInputChange?.(text);
    },
    [inputIsControlled, onInputChange],
  );

  // --- Clear selection ---
  const handleClearValue = useCallback(() => {
    onChange(null, "clear");
  }, [onChange]);

  // --- Equality check ---
  const isEqual = useCallback(
    (option: T, val: T) => {
      if (isOptionEqualToValue) return isOptionEqualToValue(option, val);
      return getOptionId(option) === getOptionId(val);
    },
    [isOptionEqualToValue, getOptionId],
  );

  // --- Filtered options ---
  const filteredOptions = useMemo(() => {
    if (filterOptions) return filterOptions(options, inputValue);
    return defaultFilter(options, inputValue, getOptionLabel);
  }, [options, inputValue, filterOptions, getOptionLabel]);

  // --- Selection ---
  const handleSelect = useCallback(
    (option: T) => {
      onChange(option, "selectOption");
      if (!inputIsControlled) {
        setInternalInputValue("");
      }
      onInputChange?.("");
    },
    [onChange, inputIsControlled, onInputChange],
  );

  // --- Combobox hook ---
  const { isOpen, close, highlightedIndex, setHighlightedIndex, containerRef, handleKeyPress, handleInputFocus } =
    useCombobox<T>({
      options: filteredOptions,
      getOptionId,
      onSelect: handleSelect,
    });

  const handleOptionPress = useCallback(
    (option: T) => {
      handleSelect(option);
      close();
    },
    [handleSelect, close],
  );

  // --- Show/hide dropdown ---
  const showDropdown = isOpen && (isLoading || filteredOptions.length > 0 || inputValue.length > 0);

  // --- Grouped options (preserves flat index for keyboard nav) ---
  const groupedOptions = useMemo(() => {
    if (!groupBy) return null;
    const groups: { label: string; items: { option: T; flatIndex: number }[] }[] = [];
    let currentGroup: { label: string; items: { option: T; flatIndex: number }[] } | undefined;
    filteredOptions.forEach((option, flatIndex) => {
      const gl = groupBy(option);
      if (!currentGroup || gl !== currentGroup.label) {
        currentGroup = { label: gl, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push({ option, flatIndex });
    });
    return groups;
  }, [groupBy, filteredOptions]);

  // --- Render ---
  const chipContent =
    value !== null ?
      <View style={styles.chip}>
        <Chip label={getOptionLabel(value)} onDelete={handleClearValue} />
      </View>
    : undefined;

  const inputSlotProps: AutocompleteInputSlotProps = {
    value: inputValue,
    onChangeText: handleInputChange,
    onFocus: handleInputFocus,
    onKeyPress: handleKeyPress,
    placeholder: value === null ? placeholder : undefined,
    label,
    textInputProps,
    leftContent: leftContent ?? chipContent,
  };

  return (
    <View ref={containerRef} style={[styles.container, showDropdown && styles.containerOpen]}>
      {renderInput ? renderInput(inputSlotProps) : <SearchInputField {...inputSlotProps} />}

      {showDropdown && (
        <>
          {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={close} />}
          <View style={styles.dropdown}>
            {isLoading ?
              renderLoading ?
                renderLoading()
              : <SearchLoading />
            : filteredOptions.length === 0 ?
              renderEmpty ?
                renderEmpty()
              : <SearchEmpty />
            : <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
                {groupedOptions ?
                  groupedOptions.map((group, groupIndex) => (
                    <View key={`${group.label}-${groupIndex.toString()}`}>
                      {renderGroup ?
                        renderGroup(group.label)
                      : <View style={styles.groupHeader}>
                          <Text textStyle="xs/semibold" color="textMuted">
                            {group.label}
                          </Text>
                        </View>
                      }
                      {group.items.map(({ option, flatIndex }) => {
                        const optionState: OptionState = {
                          focused: flatIndex === highlightedIndex,
                          selected: value !== null && isEqual(option, value),
                          index: flatIndex,
                        };

                        if (renderOption) {
                          return (
                            <Pressable
                              key={getOptionId(option)}
                              onPress={() => handleOptionPress(option)}
                              onHoverIn={() => setHighlightedIndex(flatIndex)}
                            >
                              {renderOption(option, optionState)}
                            </Pressable>
                          );
                        }

                        return (
                          <AutocompleteOption
                            key={getOptionId(option)}
                            label={getOptionLabel(option)}
                            state={optionState}
                            onPress={() => handleOptionPress(option)}
                            onHoverIn={() => setHighlightedIndex(flatIndex)}
                          />
                        );
                      })}
                    </View>
                  ))
                : filteredOptions.map((option, index) => {
                    const optionState: OptionState = {
                      focused: index === highlightedIndex,
                      selected: value !== null && isEqual(option, value),
                      index,
                    };

                    if (renderOption) {
                      return (
                        <Pressable
                          key={getOptionId(option)}
                          onPress={() => handleOptionPress(option)}
                          onHoverIn={() => setHighlightedIndex(index)}
                        >
                          {renderOption(option, optionState)}
                        </Pressable>
                      );
                    }

                    return (
                      <AutocompleteOption
                        key={getOptionId(option)}
                        label={getOptionLabel(option)}
                        state={optionState}
                        onPress={() => handleOptionPress(option)}
                        onHoverIn={() => setHighlightedIndex(index)}
                      />
                    );
                  })
                }
              </ScrollView>
            }
          </View>
        </>
      )}
    </View>
  );
}

AutocompleteInner.displayName = "Autocomplete";

const Autocomplete = AutocompleteInner as <T>(props: AutocompleteProps<T>) => ReturnType<typeof AutocompleteInner>;

export { Autocomplete };
export type { AutocompleteProps, AutocompleteInputSlotProps, OptionState } from "./types";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    overflow: "visible",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
    overflow: "visible",
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: theme.spacing.md,
    maxWidth: "40%",
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: theme.zIndex.backdrop,
  },
  dropdown: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    maxHeight: 300,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing.xl,
    ...theme.shadows.sm,
    zIndex: theme.zIndex.dropdown,
    overflow: "hidden",
  },
  list: {
    maxHeight: 300,
  },
  groupHeader: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 4,
  },
}));
